/**
 * Simple typed version of uniqBy from lodash. This accepts a comparison key from the array you pass in that is used to check for uniqueness.
 *
 * Essentially it removes duplicates from arrays based on a key.
 * @param arr Array that you want to check.
 * @param key Key from the array that you want to check.
 * @returns
 */
export function uniqueBy<T>(
  arr: T[],
  keyOrKeyFn: keyof T | ((item: T) => string | number)
): T[] {
  if (typeof keyOrKeyFn === 'function') {
    const seen = new Set<string | number>();
    return arr.filter(item => {
      const k = keyOrKeyFn(item);
      return !seen.has(k) && seen.add(k);
    });
  }
  const seen = new Set<T[keyof T]>();
  return arr.filter(item => {
    const k = item[keyOrKeyFn];
    return !seen.has(k) && seen.add(k);
  });
}

export const groupBy = <T>(
  array: T[],
  predicate: (value: T, index: number, array: T[]) => string
) =>
  array.reduce(
    (acc, value, index, array) => {
      (acc[predicate(value, index, array)] ||= []).push(value);
      return acc;
    },
    {} as { [key: string]: T[] }
  );

export const dedupeArray = <T>(array: T[]) => Array.from(new Set(array));

export const chunk = <T>(arr: T[], size: number): T[][] => {
  if (!Number.isInteger(size) || size <= 0) {
    throw new RangeError('chunk size must be a positive integer');
  }
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
};

/**
 * Updates items in a collection by matching key-value pairs and applying an update callback.
 *
 * @param collection - The array of items to update
 * @param match - An object containing key-value pairs to match
 * @param updateFn - A callback function that returns the updated item
 * @returns A new array with updated items (non-mutating)
 */
type UpdateCallback<T> = (item: T) => T;

export const updateBy = <T>(
  collection: T[],
  match: Partial<T>,
  updateFn: UpdateCallback<T>
): T[] => {
  return collection.map(item =>
    Object.entries(match).every(
      ([key, value]) => item[key as keyof T] === value
    )
      ? updateFn(item)
      : item
  );
};
